<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import numeral from 'numeral';
import { type EmitsToListeners, type UploadedFile, type ToErrorMap, type ErrorList, pickType } from '@/types/core';
import SegmentInfo from '@/views/CRM/Segments/SegmentInfo.vue';
import { useLodash } from '@/setup/lodash';
import { useApi } from '@/api';
import { useSegmentRules } from '@/views/CRM/composables/useSegmentRules';
import AddButton from '@/components/AddButton.vue';
import { Tree, TreeBranch } from '@/views/CRM/components/Tree';
import FilterComponent from '@/views/CRM/components/FilterComponent.vue';
import { segmentFilters } from '@/views/CRM/constants/segmentFilters';
import UploadFile from '@/components/UploadFile.vue';
import Comments from '@/components/Comments.vue';
import { usePermissions } from '@/composables/usePermissions';
import type { ExtractByFieldsType } from '@/types/util';
import type { PostCrmSegmentsData, PutCommentsByIdData, PutCrmSegmentsByIdData } from '@/types/openapi-ts';
import type { CrmSegment, CrmSegmentRuleGroup, CrmSegmentStats } from '@/types/crm';
//#endregion

//#region types
type SegmentRule = CrmSegmentRuleGroup['rules'][number];
type SegmentRuleNode = SegmentRule['rules'][number];

type SegmentUpdateBody = PutCrmSegmentsByIdData['body'];
type SegmentUpdateBodyTypeManual = ExtractByFieldsType<SegmentUpdateBody, 'type', 'manual'>;
type SegmentUpdateBodyTypeDynamicFixed = ExtractByFieldsType<SegmentUpdateBody, 'type', 'dynamic' | 'fixed'>;

type SegmentCreateBody = PostCrmSegmentsData['body'];
type SegmentCreateBodyTypeManual = ExtractByFieldsType<SegmentCreateBody, 'type', 'manual'>;
type SegmentCreateBodyTypeDynamicFixed = ExtractByFieldsType<SegmentCreateBody, 'type', 'dynamic' | 'fixed'>;

type FormErrorsTypeManual = ToErrorMap<SegmentUpdateBodyTypeManual & SegmentCreateBodyTypeManual>;
type FormErrorsTypeDynamicFixed =
  ToErrorMap<SegmentUpdateBodyTypeDynamicFixed & SegmentCreateBodyTypeDynamicFixed, ErrorList>;

export type SegmentFormErrorsTypeDynamicFixed =
  ToErrorMap<(SegmentUpdateBodyTypeDynamicFixed & SegmentCreateBodyTypeDynamicFixed)['rules_group']['rules'][number]['rules'][number]>;

type FormErrors = FormErrorsTypeManual | FormErrorsTypeDynamicFixed;
type SegmentMutationBody = (SegmentCreateBody | SegmentUpdateBody) & { site_id?: string };

type SegmentForm = Omit<Partial<CrmSegment>, 'type' | 'rules_group'> & {
  type: string;
  files?: UploadedFile[];
  players_ids?: string | string[];
  players_count?: number;
  site_id?: string;
  rules_group: CrmSegmentRuleGroup;
};

interface Props {
  segmentId?: string | null;
  currentBrand: string;
  isLocalSegment?: boolean;
  localSegment?: Record<string, any> | null;
  localSegmentErrors?: Record<string, any>;
}

interface Emits {
  (event: 'close'): void;
  (event: 'updateLocalSegment', segment: SegmentMutationBody | SegmentForm, stats: CrmSegmentStats | null): void;
}

export interface CreateSegmentParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  segmentId: null,
  isLocalSegment: false,
  localSegment: null,
  localSegmentErrors: () => ({}),
});

const emit = defineEmits<Emits>();
//#endregion

//#region const
const segmentTemplate: SegmentForm = {
  id: '',
  name: '',
  type: 'fixed',
  files: [],
  players_ids: '',
  players_count: 0,
  rules_group: {
    logical_relations: 'and',
    rules: [
      {
        logical_relations: 'and',
        rules: [
          {
            rule_type: '',
            operator: '',
            values: '',
          },
        ],
      },
    ],
  },
};
const filter: SegmentRuleNode = {
  rule_type: '',
  operator: '',
  values: '',
};
const rule: SegmentRule= {
  logical_relations: 'and',
  rules: [],
};
//#endregion

//#region composables
const api = useApi();
const _ = useLodash();
const { t } = useI18n();
const { clearSegment } = useSegmentRules();
const { hasPermission } = usePermissions();
//#endregion

//#region state
const isLoading = ref(false);
const isVisible = ref(false);
const allowChangeManualSegment = ref(true);
const isSaveLoading = ref(false);
const errors = ref<FormErrors>({});
const isSegmentInfoLoading = ref(false);
const filterLoading = ref(false);
const segment = ref<SegmentForm>(_.cloneDeep(segmentTemplate));
const stats = ref<CrmSegmentStats | null>(null);
const statsLoadingCount = ref(0);
const manual = ref({
  type: 'file',
  files: [] as UploadedFile[],
});
//#endregion

//#region computed
const isStatsLoading = computed(() => statsLoadingCount.value !== 0);
const isActive = computed(() => !isLoading.value && !isStatsLoading.value && !isSaveLoading.value);
const playersCount = computed(() => numeral(segment.value.players_count).format('0,0'));

const isEdit = computed(() => {
  return !!props.segmentId;
});

const pPopupTitle = computed(() => {
  return t(`crm.segments.${isEdit.value ? 'edit' : 'create'}_segment_title`);
});

const segmentTypes = computed(() => ([
  { value: 'fixed', name: t('crm.segments.type.fixed') },
  { value: 'dynamic', name: t('crm.segments.type.dynamic') },
  { value: 'manual', name: t('crm.segments.type.manual') },
]));

const logicalList = computed(() => ([
  { value: 'and', name: t('crm.segments.logical_relations.and') },
  { value: 'or', name: t('crm.segments.logical_relations.or') },
]));

const availableSegmentFilters = computed(() => {
  if (hasPermission('affiliates_view')) {
    return segmentFilters;
  }

  return segmentFilters.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      return item.name !== 'player_affiliate_id' && item.name !== 'player_sub_affiliate_id';
    }),
  }));
});

const preparedSegmentData = computed<SegmentMutationBody>(() => {
  const segmentValue = clearSegment(segment.value);

  if (segmentValue.type === 'manual') {
    if (manual.value.type === 'file') {
      const { files, ...restSegment } = _.omit(segmentValue, ['players_ids', 'rules_group']);
      const fileId = _.get(segmentValue, ['files', 0, 'uploadId']);

      return {
        ...restSegment,
        files,
        ...((!isEdit.value || fileId) && { players_file_id: fileId }),
      } as SegmentMutationBody;
    }

    if (manual.value.type === 'text') {
      const { players_ids: playersIds = '', ...restSegment } = _.omit(segmentValue, ['players', 'rules_group']);
      const ids = String(playersIds).trim().split('\n')
        .map((player: string) => player.trim())
        .filter((player: string) => player);

      return {
        ...restSegment,
        ...((!isEdit.value || ids.length) && { players_ids: ids }),
      } as SegmentMutationBody;
    }
  }

  return _.omit(segmentValue, ['players', 'players_ids']) as SegmentMutationBody;
});
//#endregion

//#region methods
async function open() {
  isVisible.value = true;
  if (props.isLocalSegment && props.localSegment) {
    const { players_ids: playersIds = '' } = props.localSegment;

    segment.value = _.cloneDeep({
      ...props.localSegment,
      type: props.localSegment.type || segmentTemplate.type,
      files: [],
      players_ids: Array.isArray(playersIds) ? playersIds.join('\n') : playersIds,
      rules_group: {
        ...segmentTemplate.rules_group,
        ...props.localSegment.rules_group,
      },
    });
    errors.value = _.cloneDeep(props.localSegmentErrors);
    await checkChanges();
  } else {
    segment.value = _.cloneDeep(segmentTemplate);
    await checkChanges();
  }

  if (isEdit.value && !props.isLocalSegment) {
    await getSegment();
  }
  allowChangeManualSegment.value = segment.value.type !== 'manual';
}

function close() {
  isVisible.value = false;
  emit('close');
}

async function getSegment() {
  isLoading.value = true;
  isSegmentInfoLoading.value = true;

  const { data: { payload } } = await api.getSegment(props.segmentId!);

  segment.value = _.cloneDeep({
    ...payload,
    rules_group: {
      ...segmentTemplate.rules_group,
      ...payload.rules_group,
    },
  });

  await checkChanges();
  await getComments();
  await nextTick();

  isLoading.value = false;
  isSegmentInfoLoading.value = false;
}

async function getStats(segmentRules: SegmentForm) {
  statsLoadingCount.value += 1;
  try {
    segmentRules.site_id = props.currentBrand;
    const { data: { payload } } = props.segmentId && segment.value.type === 'manual'
      ? await api.getSegmentStatsById(props.segmentId)
      : await api.getSegmentStats(segmentRules as any);
    stats.value = payload;
  } finally {
    statsLoadingCount.value -= 1;
  }
}

async function checkChanges(value: SegmentForm = segment.value) {
  if (!_.isEmpty(value) && value.type !== 'manual') {
    const checkSegment = clearSegment(value);
    if (checkSegment.rules_group.rules?.length !== 0) {
      await getStats(checkSegment);
    } else {
      stats.value = null;
    }
  }
}

function addNewRuleGroup() {
  const ruleValue = _.cloneDeep(rule);
  const filterValue = _.cloneDeep(filter);
  ruleValue.rules.push(filterValue);
  segment.value.rules_group.rules.push(ruleValue);
}

function errorFiltersBlock(index: number) {
  if (pickType<FormErrorsTypeDynamicFixed>().typeGuard(errors.value)
    && !_.isEmpty(errors.value)
    && errors.value.rules_group
    && 'rules' in errors.value.rules_group
    && errors.value.rules_group.rules
    && Array.isArray(errors.value.rules_group.rules)
    && errors.value.rules_group.rules[index]
    && 'message' in errors.value.rules_group.rules[index]
  ) {
    return errors.value.rules_group.rules[index];
  }

  return {} as ErrorList;
}
function errorFilters(index: number, groupRuleId: number) {
  if (pickType<FormErrorsTypeDynamicFixed>().typeGuard(errors.value)
    && !_.isEmpty(errors.value)
    && errors.value.rules_group
    && 'rules' in errors.value.rules_group
    && errors.value.rules_group.rules
    && Array.isArray(errors.value.rules_group.rules)
    && errors.value.rules_group.rules[index]
    && 'rules' in errors.value.rules_group.rules[index]
    && errors.value.rules_group.rules[index].rules
  ) {
    return errors.value.rules_group.rules[index].rules[groupRuleId];
  }

  if (pickType<FormErrorsTypeDynamicFixed>().typeGuard(errors.value)
    && !_.isEmpty(errors.value)
    && 'rules_group' in errors.value
    && errors.value.rules_group
    && 'rules' in errors.value.rules_group
    && errors.value.rules_group.rules
    && Array.isArray(errors.value.rules_group.rules)
    && errors.value.rules_group.rules[index]
    && 'message' in errors.value.rules_group.rules[index]
  ) {
    return errors.value.rules_group.rules[index];
  }
  return {};
}

function addNewFilter(id: number) {
  const filterValue = _.cloneDeep(filter);
  const targetGroup = segment.value.rules_group.rules[id];
  if (!targetGroup) return;
  targetGroup.rules.push(filterValue);
}

function deleteFilter(rulesId: number, filterId: number) {
  const rulesList = segment.value.rules_group.rules;
  const currentGroup = rulesList[rulesId];
  if (!currentGroup) return;
  const filterList = currentGroup.rules;
  if (filterList.length !== 1 || rulesList.length !== 1) {
    filterList.splice(filterId, 1);
  }
  if (filterList.length === 0) {
    rulesList.splice(rulesId, 1);
    if(pickType<FormErrorsTypeDynamicFixed>().typeGuard(errors.value)
        && !_.isEmpty(errors.value)
        && errors.value.rules_group
        && 'rules' in errors.value.rules_group
        && errors.value.rules_group.rules
        && typeof errors.value.rules_group.rules === 'object'
      ) {
      errors.value.rules_group.rules.splice(rulesId, 1);
    }
  }
}

function getComments() {
  return api.getSegmentComments(props.segmentId!);
}

function postComment(comment: PutCommentsByIdData['body']) {
  return api.postSegmentComments(props.segmentId!, comment);
}

async function handleSave(toReview = false) {
  if (!props.isLocalSegment) {
    isSaveLoading.value = true;
    segment.value.site_id = props.currentBrand;
    let localSegmentId = '';
    try {
      if (!isEdit.value) {
        const { data: { payload: { id } } } = await api.postSegments(preparedSegmentData.value as SegmentCreateBody);
        localSegmentId = id;
      } else {
        const { data: { payload: { id } } } = await api.putSegment(
          props.segmentId!,
          preparedSegmentData.value as SegmentUpdateBody,
        );
        localSegmentId = id;
      }
      if(toReview) {
        await api.postSegmentChangeStatus(localSegmentId, 'to-review');
      }
      close();
    } catch (error: any) {
      if (
        error.data.code === 'WRONG_FILE_FORMAT_ERROR'
        && pickType<FormErrorsTypeDynamicFixed>().typeGuard(errors.value) && 'players_file_id' in errors.value
      ) {
        errors.value.players_file_id = error.data;
      } else {
        errors.value = error.data?.errors;
      }
    } finally {
      isSaveLoading.value = false;
    }
  } else {
    const { type } = segment.value;
    emit('updateLocalSegment', type === 'manual' ? preparedSegmentData.value : segment.value, stats.value);
    close();
  }
}

function replaceSegment() {
  allowChangeManualSegment.value = true;
}
//#endregion

defineExpose({
  open,
  close,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="pPopupTitle"
    :width="1140"
    class="create-segment"
    :is-loading="isLoading"
    @close="close"
  >
    <template v-slot:body>
      <div class="t:flex t:gap-32">
        <div class="create-segment__block t:space-y-16">
          <div class="t:flex t:items-start t:gap-12">
            <UiTabs
              v-model="segment.type"
              :list="segmentTypes"
              :label="$t('crm.segments.type.title')"
              :disabled="isSaveLoading"
              size="big"
            />
            <UiInput
              v-model="segment.name"
              :label="$t('crm.segments.create_segment_name_input')"
              autosize
              size="big"
              :placeholder="$t('crm.segments.create_segment_name_input_placeholder')"
              type="text"
              :error="errors.name"
            />
          </div>
          <div
            v-if="['fixed', 'dynamic'].includes(segment.type)"
            v-loading="isSegmentInfoLoading || filterLoading"
            class="t:space-y-16"
          >
            <UiLabel
              :label="$t('crm.segments.configure.title')"
              :font-size="16"
              bold
            />
            <Tree
              type="outer"
            >
              <TreeBranch>
                <UiTabs
                  v-model="segment.rules_group.logical_relations"
                  :list="logicalList"
                  size="small"
                  width="auto"
                />
              </TreeBranch>
              <TreeBranch
                v-for="(group, id) in segment.rules_group.rules"
                :key="id"
                class="t:pt-12"
              >
                <FormRow
                  :error="!_.isEmpty(errorFiltersBlock(+id))"
                >
                  <TreeBranch>
                    <UiTabs
                      v-model="group.logical_relations"
                      :list="logicalList"
                      width="auto"
                      size="small"
                    />
                  </TreeBranch>
                  <TreeBranch
                    v-for="(group_rule, group_rule_id) in group.rules"
                    :key="group_rule_id"
                  >
                    <FilterComponent
                      :available-filters="availableSegmentFilters"
                      :group-rule="group_rule"
                      :current-brand="currentBrand"
                      :errors="errorFilters(+id, +group_rule_id)"
                      @loading="filterLoading = $event"
                      @delete-filter="deleteFilter(+id, +group_rule_id)"
                    />
                  </TreeBranch>
                  <span
                    class="t:pl-8 t:mt-8 t:cursor-pointer t:flex t:items-center t:gap-8"
                    @click="addNewFilter(+id)"
                  >
                    <UiButton
                      is-icon
                      icon="plus"
                    />
                    <UiLabel
                      :label="$t('crm.segments.configure.add_new_rule')"
                      :font-size="12"
                      bold
                    />
                  </span>
                </FormRow>
                <UiHelp
                  v-if="!_.isEmpty(errorFiltersBlock(+id))"
                  :is-error="true"
                  :text="errorFiltersBlock(+id).message"
                />
              </TreeBranch>
            </Tree>
            <AddButton
              @click="addNewRuleGroup"
            >
              {{ $t('crm.segments.configure.add_new_group') }}
            </AddButton>
          </div>
          <div
            v-if="segment.type === 'manual'"
            class="t:space-y-16"
          >
            <UiLabel
              :label="$t('crm.segments.manual.title')"
              :font-size="16"
              bold
            />
            <CardTitle
              v-if="!allowChangeManualSegment"
              :title="$t('crm.segments.manual.replaceNotice', { playersCount })"
            >
              <UiAction
                icon="sync-alt"
                :action-name="$t('crm.segments.manual.replace')"
                @click="replaceSegment"
              />
            </CardTitle>
            <UiRadioGroup
              v-model="manual.type"
              :disabled="!allowChangeManualSegment"
              :options="[
                {
                  label: $t(`crm.segments.manual.type.csv`),
                  value: 'file',
                },
                {
                  label: $t(`crm.segments.manual.type.manual`),
                  value: 'text',
                }
              ]"
            />
            <template
              v-if="manual.type === 'file'"
            >
              <UploadFile
                v-model="segment.files"
                :title="$t('crm.segments.manual.csv.title')"
                accept=".csv"
                :limit-file="1"
                is-dragger
                :error="pickType<FormErrorsTypeManual>().from(errors).players_file_id"
                :disabled="!allowChangeManualSegment"
              >
                <template
                  v-if="allowChangeManualSegment"
                  v-slot:note
                >
                  <div
                    class="t:flex t:items-start t:gap-12"
                  >
                    <UiIcon
                      name="exclamation-triangle"
                      color="var(--text-warning)"
                      :size="24"
                    />
                    <div v-html="$t('crm.segments.manual.csv.note')" />
                  </div>
                </template>
              </UploadFile>
            </template>
            <UiTextarea
              v-if="manual.type === 'text'"
              v-model="segment.players_ids"
              :label="$t(`crm.segments.manual.text.title`)"
              :placeholder="$t(`crm.segments.manual.text.placeholder`)"
              :rows="10"
              :disabled="!allowChangeManualSegment"
              :error="pickType<FormErrorsTypeManual>().from(errors).players_ids"
            />
          </div>
          <Comments
            v-if="isEdit && !isLocalSegment"
            :is-editable="true"
            :get-method="getComments"
            :post-method="postComment"
          />
        </div>
        <SegmentInfo
          v-if="segment.type !== 'manual'"
          class="create-segment__block create-segment__block_side"
          :stats="stats || {}"
          :stats-loader="isStatsLoading"
          @refresh-stats="checkChanges"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          :disabled="isLoading || isStatsLoading || isSaveLoading"
          @click="close()"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          icon="check"
          :disabled="!isActive"
          @click="handleSave()"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
        <UiButton
          v-if="!props.isLocalSegment"
          icon="check-double"
          filled
          :disabled="!isActive"
          @click="handleSave(true)"
        >
          {{ $t('crm.buttons.save_and_send') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.create-segment {
  &__block {
    width: 100%;
    &_side {
      flex-shrink: 0;
      width: 270px;
    }
  }
}
</style>
